import cn from 'classnames';
import React, {
  FC,
  KeyboardEvent,
  MouseEvent,
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  LibTooltipPosition,
  PortalTooltip,
} from '@infologistics/frontend-libraries';

const HINT_OPEN_DELAY_MS = 500;
const TOOLTIP_ROOT_ID = 'system-tooltip-root';
const HINT_Z_INDEX = 1;

const ensureTooltipRoot = (): string => {
  let root = document.getElementById(TOOLTIP_ROOT_ID);

  if (!root) {
    root = document.createElement('div');
    root.id = TOOLTIP_ROOT_ID;
    document.body.appendChild(root);
  }

  root.style.inset = '0';
  root.style.pointerEvents = 'none';
  root.style.position = 'fixed';
  root.style.zIndex = '100000';

  return TOOLTIP_ROOT_ID;
};

interface IProps {
  children: ReactElement<any>;
  content?: ReactNode;
  position?: LibTooltipPosition;
  tooltipClasses?: string;
  wrapperClasses?: string;
}

const SystemHint: FC<IProps> = ({
  children,
  content,
  position = LibTooltipPosition.RIGHT,
  tooltipClasses,
  wrapperClasses,
}) => {
  const openTimerRef = useRef<number | null>(null);
  const targetRef = useRef<HTMLSpanElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [tooltipContainerId, setTooltipContainerId] = useState('app-root');

  const clearOpenTimer = (): void => {
    if (openTimerRef.current === null) return;

    window.clearTimeout(openTimerRef.current);
    openTimerRef.current = null;
  };

  const openHint = (): void => {
    clearOpenTimer();
    setIsOpen(true);
  };

  const closeHint = (): void => {
    clearOpenTimer();
    setIsOpen(false);
  };

  const scheduleOpenHint = (): void => {
    clearOpenTimer();
    openTimerRef.current = window.setTimeout(openHint, HINT_OPEN_DELAY_MS);
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleDocumentMouseDown = (evt: globalThis.MouseEvent): void => {
      const target = evt.target as Node | null;

      if (target && targetRef.current?.contains(target)) return;

      closeHint();
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);

    return () => document.removeEventListener('mousedown', handleDocumentMouseDown);
  }, [isOpen]);

  useEffect(() => () => clearOpenTimer(), []);

  useEffect(() => {
    if (!content) return;

    setTooltipContainerId(ensureTooltipRoot());
  }, [content]);

  if (!content) return children;

  const handleClick = (evt: MouseEvent<HTMLSpanElement>): void => {
    evt.preventDefault();
    evt.stopPropagation();

    clearOpenTimer();
    setIsOpen((value) => !value);
  };

  const handleKeyDown = (evt: KeyboardEvent<HTMLSpanElement>): void => {
    if (evt.key !== 'Escape') return;

    closeHint();
  };

  return (
    <span
      className={cn('d-inline-flex relative', wrapperClasses)}
      onClick={handleClick}
      onBlur={closeHint}
      onFocus={scheduleOpenHint}
      onKeyDown={handleKeyDown}
      onMouseEnter={scheduleOpenHint}
      onMouseLeave={closeHint}
      ref={targetRef}
      style={{ cursor: 'help' }}
      tabIndex={0}
    >
      {React.cloneElement(children, { hint: undefined })}
      {isOpen && (
        <PortalTooltip
          classes={cn('font-xs fw-400', tooltipClasses)}
          containerId={tooltipContainerId}
          maxWidth='360px'
          position={position}
          targetRef={targetRef}
          zIndex={HINT_Z_INDEX}
        >
          {content}
        </PortalTooltip>
      )}
    </span>
  );
};

export default SystemHint;
